import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchPrivacyNotice } from '@/api/endpoints/privacy';
import type { PrivacyNotice } from '@/types/api';

function Section({ title, items }: { title: string; items: string[] }) {
  return (
    <section className="mt-6">
      <h2 className="text-sm font-semibold text-ink">{title}</h2>
      <ul className="mt-2 list-disc space-y-1.5 pl-5 text-sm text-ink-secondary">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </section>
  );
}

/** Public. The notice a person agrees to at signup, served by the API so the version
 *  they accepted and the text they read cannot drift apart. */
export function PrivacyNoticePage() {
  const { data, isPending, isError } = useQuery<PrivacyNotice>({
    queryKey: ['privacy', 'notice'],
    queryFn: fetchPrivacyNotice,
  });

  return (
    <div className="min-h-screen bg-canvas px-4 py-10">
      <article className="mx-auto max-w-2xl rounded-[--radius-card] border border-line bg-surface p-6 shadow-sm">
        <h1 className="text-lg font-semibold text-ink">Privacy notice</h1>
        {isPending && <p className="mt-4 text-sm text-ink-secondary">Loading…</p>}
        {isError && (
          <p className="mt-4 text-sm text-bad" role="alert">
            The notice could not be loaded. Please try again.
          </p>
        )}
        {data && (
          <>
            <p className="mt-1 text-[13px] text-ink-muted">
              Version {data.version} · Data controller: {data.controller}
            </p>
            <Section title="What we collect" items={data.data_collected} />
            <Section title="Why" items={data.purposes} />
            <Section title="Who receives it" items={data.recipients} />
            <Section title="How long we keep it" items={data.retention} />
            <Section title="Your rights" items={data.rights} />
            <section className="mt-6">
              <h2 className="text-sm font-semibold text-ink">Grievances</h2>
              <p className="mt-2 text-sm text-ink-secondary">
                {data.grievance_email ? (
                  <>
                    Write to{' '}
                    <a className="font-medium text-accent" href={`mailto:${data.grievance_email}`}>
                      {data.grievance_email}
                    </a>
                    .
                  </>
                ) : (
                  'Contact the operator of this service.'
                )}
              </p>
            </section>
          </>
        )}
        <p className="mt-8 text-[13px]">
          <Link to="/signup" className="font-medium text-accent hover:text-accent-hover">
            Back to sign up
          </Link>
        </p>
      </article>
    </div>
  );
}
